import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, Waves } from "lucide-react";
import ChapterHeading from "@/components/landing/ChapterHeading";

const FEATURES = [
  "Piscina SPA gonfiabile riscaldata, aperta tutto l'anno",
  "Idromassaggio all'aperto, anche sotto le stelle del borgo",
  "Giardino privato con prato, amaca e sedie all'ombra",
  "Terrazza con vista sul cortile interno e sul giardino",
  "Pranzi e cene all'aperto tra i muri in pietra",
  "Accessibile a ogni età",
];

export default function GiardinoSpa() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="spa" data-testid="spa-section" ref={ref} className="relative overflow-hidden bg-olive py-24 text-linen sm:py-36">
      <div className="pointer-events-none absolute -right-20 top-10 select-none font-serif text-[16rem] italic leading-none text-linen/5">
        spa
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <ChapterHeading
          eyebrow="Capitolo 03"
          light
          title="Giardino & Piscina SPA"
          subtitle="Il giardino, chiuso dalle mura secolari, è sereno e riservato: e la sera, un bagno caldo all'aperto sotto le stelle."
        />

        <div className="grid items-center gap-12 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5"
          >
            <div className="mb-8 inline-flex items-center gap-3 rounded-full border border-linen/20 bg-linen/10 px-5 py-2 backdrop-blur-md">
              <Waves size={16} className="text-oro" />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em]">Aperta tutto l'anno · Riscaldata</span>
            </div>
            <ul className="space-y-4">
              {FEATURES.map((f, i) => (
                <motion.li
                  key={f}
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="flex items-start gap-3 text-base font-light text-linen/90"
                >
                  <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-oro/20 text-oro">
                    <Check size={12} />
                  </span>
                  {f}
                </motion.li>
              ))}
            </ul>
            <p className="mt-8 border-l-2 border-oro/60 pl-5 font-serif text-lg italic text-linen/80">
              “La sera, quando il borgo si spegne, restano il vapore dell'acqua calda e il cielo dell'Umbria.”
            </p>
          </motion.div>

          <div className="relative lg:col-span-7">
            <motion.div style={{ y }} className="relative">
              <img
                src="/photos/spa.webp"
                alt="La piscina SPA gonfiabile riscaldata sotto la pergola in pietra"
                loading="lazy"
                className="aspect-[4/3] w-full rounded-sm object-cover shadow-2xl shadow-black/40"
              />
            </motion.div>
            <motion.img
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9, delay: 0.2 }}
              src="/photos/amaca.webp"
              alt="Il giardino dall'alto con amaca e sedie"
              loading="lazy"
              className="absolute -bottom-14 -left-4 hidden w-2/5 rounded-sm border-4 border-olive object-cover shadow-2xl shadow-black/40 sm:block"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
